import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatusBadge from '../../components/StatusBadge';

export default function Drivers() {
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0 });
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchDrivers();
    fetchSupervisors();
  }, []);

  const fetchDrivers = async (page = 1) => {
    try {
      const response = await api.get('/admin/drivers', { params: { page, search, limit: 20 } });
      setDrivers(response.data.data || []);
      setPagination(response.data.pagination || { page: 1, total: 0 });
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
      toast.error('Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  const fetchSupervisors = async () => {
    try {
      const response = await api.get('/admin/supervisors');
      setSupervisors(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch supervisors:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchDrivers(1);
  };

  const handleSupervisorChange = async (driverId, supervisorId) => {
    try {
      await api.put(`/admin/drivers/${driverId}`, {
        supervisor_id: supervisorId || null
      });
      toast.success('Supervisor updated');
      fetchDrivers(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to update supervisor');
    }
  };

  const handleStatusChange = async (driverId, status) => {
    try {
      await api.put(`/admin/drivers/${driverId}`, { status });
      toast.success('Status updated');
      fetchDrivers(pagination.page);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Driver Management</h1>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex space-x-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="flex-1 max-w-md"
        />
        <button type="submit" className="btn btn-primary">
          Search
        </button>
      </form>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          Assign supervisors to drivers so they can approve their log entries.
          Supervisors can only see and approve logs from drivers assigned to them.
        </p>
      </div>

      {/* Drivers Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supervisor</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hire Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {drivers.map((driver) => (
              <tr key={driver.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white text-sm">
                      {driver.user?.first_name?.[0]?.toUpperCase() || 'D'}
                    </div>
                    <div className="ml-3">
                      <div className="font-medium text-gray-900">
                        {driver.user?.first_name} {driver.user?.last_name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  {driver.user?.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={driver.supervisor_id || ''}
                    onChange={(e) => handleSupervisorChange(driver.id, e.target.value)}
                    className="text-sm border-gray-300 rounded-md"
                  >
                    <option value="">No Supervisor</option>
                    {supervisors.map((sup) => (
                      <option key={sup.id} value={sup.id}>
                        {sup.first_name} {sup.last_name} ({sup.role?.name})
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={driver.status}
                    onChange={(e) => handleStatusChange(driver.id, e.target.value)}
                    className="text-sm border-gray-300 rounded-md"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  {driver.hire_date ? new Date(driver.hire_date).toLocaleDateString() : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {drivers.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No drivers found
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {drivers.length} of {pagination.total} drivers
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
